import { useState, useEffect } from 'react';
import { Plus, Users, Edit, Trash2, Search, Car, X, KeyRound, Link as LinkIcon, Copy, MessageCircle, UserCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { toast } from 'sonner';
import ClientePerfilModal from '@/components/ClientePerfilModal';

function Clientes() {
  const [clientes, setClientes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [linkDialog, setLinkDialog] = useState({ open: false, url: '', cliente: null });
  const [perfilModal, setPerfilModal] = useState({ open: false, clienteId: null });
  const [formData, setFormData] = useState({
    nome: '',
    cpf_cnpj: '',
    telefone: '',
    email: '',
    endereco: '',
    senha_portal: ''
  });
  const [veiculos, setVeiculos] = useState([]);
  const [showVeiculoForm, setShowVeiculoForm] = useState(false);
  const [veiculoForm, setVeiculoForm] = useState({
    placa: '',
    marca: '',
    modelo: '',
    ano: '',
    cor: ''
  });

  useEffect(() => {
    loadClientes();
  }, []);

  const loadClientes = async () => {
    try {
      const { data } = await api.get('/clientes');
      setClientes(data);
    } catch (error) {
      toast.error('Erro ao carregar clientes');
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Ao editar, senha só é enviada se preenchida (evita sobrescrever com vazio)
      const payload = { ...formData };
      if (editingCliente && !payload.senha_portal) {
        delete payload.senha_portal;
      }
      let clienteId;
      if (editingCliente) {
        await api.put(`/clientes/${editingCliente.id}`, payload);
        clienteId = editingCliente.id;
        toast.success('Cliente atualizado com sucesso');
      } else {
        const response = await api.post('/clientes', payload);
        clienteId = response.data.id;
        toast.success('Cliente cadastrado com sucesso');
        
        // Cadastrar veículos se houver
        if (veiculos.length > 0) {
          for (const veiculo of veiculos) {
            await api.post('/veiculos', {
              ...veiculo,
              cliente_id: clienteId
            });
          }
          toast.success(`${veiculos.length} veículo(s) cadastrado(s)`);
        }
      }
      setIsDialogOpen(false);
      resetForm();
      loadClientes();
    } catch (error) {
      toast.error('Erro ao salvar cliente');
      console.error(error);
    }
  };

  const handleEdit = (cliente) => {
    setEditingCliente(cliente);
    setFormData({
      nome: cliente.nome,
      cpf_cnpj: cliente.cpf_cnpj || '',
      telefone: cliente.telefone || '',
      email: cliente.email || '',
      endereco: cliente.endereco || '',
      senha_portal: ''
    });
    setIsDialogOpen(true);
  };

  const gerarLinkMagico = async (cliente) => {
    try {
      const { data } = await api.post(`/clientes/${cliente.id}/link-magico`);
      const url = `${window.location.origin}/oficina/cliente?t=${data.token}`;
      setLinkDialog({ open: true, url, cliente });
    } catch {
      toast.error('Erro ao gerar link');
    }
  };

  const copiarLink = () => {
    navigator.clipboard.writeText(linkDialog.url).then(() => {
      toast.success('Link copiado!');
    });
  };

  const abrirWhatsApp = () => {
    const fone = (linkDialog.cliente?.telefone || '').replace(/\D/g, '');
    if (!fone) { toast.error('Cliente sem telefone cadastrado'); return; }
    const msg = encodeURIComponent(`Olá ${linkDialog.cliente.nome}! Acesse seu portal da oficina: ${linkDialog.url}`);
    const url = `https://wa.me/${fone.startsWith('55') ? fone : '55' + fone}?text=${msg}`;
    window.open(url, '_blank');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja deletar este cliente?')) {
      try {
        await api.delete(`/clientes/${id}`);
        toast.success('Cliente deletado com sucesso');
        loadClientes();
      } catch (error) {
        toast.error('Erro ao deletar cliente');
        console.error(error);
      }
    }
  };

  const resetForm = () => {
    setFormData({ nome: '', cpf_cnpj: '', telefone: '', email: '', endereco: '', senha_portal: '' });
    setEditingCliente(null);
    setVeiculos([]);
    setShowVeiculoForm(false);
    setVeiculoForm({ placa: '', marca: '', modelo: '', ano: '', cor: '' });
  };

  const handleAddVeiculo = () => {
    if (veiculoForm.placa && veiculoForm.marca && veiculoForm.modelo) {
      setVeiculos([...veiculos, { ...veiculoForm, ano: veiculoForm.ano ? parseInt(veiculoForm.ano) : null }]);
      setVeiculoForm({ placa: '', marca: '', modelo: '', ano: '', cor: '' });
      setShowVeiculoForm(false);
      toast.success('Veículo adicionado à lista');
    }
  };

  const handleRemoveVeiculo = (index) => {
    setVeiculos(veiculos.filter((_, i) => i !== index));
  };

  const filteredClientes = clientes.filter(c =>
    c.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.telefone && c.telefone.includes(searchTerm))
  );

  return (
    <div className="p-8" data-testid="clientes-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-white uppercase tracking-tight mb-2">Clientes</h1>
          <p className="text-slate-500">Gerencie sua base de clientes</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-electric-blue hover:bg-blue-600 text-white rounded-sm" data-testid="add-cliente-btn">
              <Plus className="w-4 h-4 mr-2" />
              Novo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-carbon border-white/10 text-white max-w-2xl" aria-describedby="dialog-description">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold uppercase">
                {editingCliente ? 'Editar Cliente' : 'Novo Cliente'}
              </DialogTitle>
              <p id="dialog-description" className="text-sm text-slate-500 sr-only">
                Formulário para {editingCliente ? 'editar' : 'cadastrar'} cliente
              </p>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Nome Completo</Label>
                <Input
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="bg-obsidian border-white/10 text-white"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">CPF/CNPJ</Label>
                  <Input
                    value={formData.cpf_cnpj}
                    onChange={(e) => setFormData({ ...formData, cpf_cnpj: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Telefone</Label>
                  <Input
                    value={formData.telefone}
                    onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-obsidian border-white/10 text-white"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Endereço</Label>
                <Input
                  value={formData.endereco}
                  onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                  className="bg-obsidian border-white/10 text-white"
                />
              </div>

              {/* Acesso ao Portal do Cliente */}
              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <KeyRound className="w-4 h-4 text-electric-blue" />
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Acesso ao Portal do Cliente</Label>
                </div>
                <Input
                  type="password"
                  value={formData.senha_portal}
                  onChange={(e) => setFormData({ ...formData, senha_portal: e.target.value })}
                  className="bg-obsidian border-white/10 text-white"
                  placeholder={editingCliente ? 'Deixe em branco para manter a senha atual' : 'Defina uma senha (cliente poderá alterá-la depois)'}
                  data-testid="cliente-senha-portal"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {editingCliente && editingCliente.tem_senha_portal
                    ? '✓ Cliente já tem acesso ao portal. Preencha para definir uma nova senha.'
                    : 'Com senha, o cliente entra em /oficina/cliente usando o CPF acima.'}
                </p>
              </div>

              {/* Seção de Veículos */}
              {!editingCliente && (
                <div className="border-t border-white/10 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-xs uppercase tracking-wider text-slate-400">Veículos do Cliente (Opcional)</Label>
                    {!showVeiculoForm && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowVeiculoForm(true)}
                        className="text-electric-blue hover:text-white hover:bg-electric-blue/10"
                      >
                        <Car className="w-4 h-4 mr-1" />
                        Adicionar Veículo
                      </Button>
                    )}
                  </div>

                  {/* Lista de veículos adicionados */}
                  {veiculos.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {veiculos.map((veiculo, index) => (
                        <div key={veiculo.id || veiculo.placa || `veic-${index}`} className="flex items-center justify-between bg-obsidian p-3 rounded-sm">
                          <div>
                            <p className="text-sm text-white font-medium">{veiculo.marca} {veiculo.modelo}</p>
                            <p className="text-xs text-slate-500">
                              {veiculo.placa} {veiculo.ano && `• ${veiculo.ano}`} {veiculo.cor && `• ${veiculo.cor}`}
                            </p>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveVeiculo(index)}
                            className="text-alert-red hover:text-white hover:bg-alert-red/10"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Formulário de veículo */}
                  {showVeiculoForm && (
                    <div className="bg-obsidian p-4 rounded-md space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-slate-400">Placa</Label>
                          <Input
                            value={veiculoForm.placa}
                            onChange={(e) => setVeiculoForm({ ...veiculoForm, placa: e.target.value.toUpperCase() })}
                            className="bg-carbon border-white/10 text-white"
                           />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-slate-400">Cor</Label>
                          <Input
                            value={veiculoForm.cor}
                            onChange={(e) => setVeiculoForm({ ...veiculoForm, cor: e.target.value })}
                            className="bg-carbon border-white/10 text-white"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-slate-400">Marca</Label>
                          <Input
                            value={veiculoForm.marca}
                            onChange={(e) => setVeiculoForm({ ...veiculoForm, marca: e.target.value })}
                            className="bg-carbon border-white/10 text-white"
                           />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-slate-400">Modelo</Label>
                          <Input
                            value={veiculoForm.modelo}
                            onChange={(e) => setVeiculoForm({ ...veiculoForm, modelo: e.target.value })}
                            className="bg-carbon border-white/10 text-white"
                            />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-slate-400">Ano</Label>
                          <Input
                            type="number"
                            value={veiculoForm.ano}
                            onChange={(e) => setVeiculoForm({ ...veiculoForm, ano: e.target.value })}
                            className="bg-carbon border-white/10 text-white"
                            />
                        </div>
                      </div>
                      <div className="flex justify-end space-x-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setShowVeiculoForm(false);
                            setVeiculoForm({ placa: '', marca: '', modelo: '', ano: '', cor: '' });
                          }}
                          className="text-slate-400"
                        >
                          Cancelar
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleAddVeiculo}
                          disabled={!veiculoForm.placa || !veiculoForm.marca || !veiculoForm.modelo}
                          className="bg-electric-blue hover:bg-blue-600 text-white"
                        >
                          Adicionar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)} className="text-slate-400">
                  Cancelar
                </Button>
                <Button type="submit" className="bg-electric-blue hover:bg-blue-600 text-white" data-testid="submit-cliente-btn">
                  {editingCliente ? 'Atualizar' : 'Cadastrar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 w-5 h-5" />
          <Input
            type="text"
            placeholder="Buscar por nome ou telefone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-carbon border-white/10 text-white"
            data-testid="search-clientes-input"
          />
        </div>
      </div>

      <div className="bg-carbon border border-white/5 rounded-md overflow-hidden shadow-lg">
        <table className="w-full">
          <thead className="bg-white/2 border-b border-white/5">
            <tr>
              <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Nome</th>
              <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Telefone</th>
              <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Email</th>
              <th className="text-right px-6 py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredClientes.length === 0 ? (
              <tr>
                <td colSpan="4" className="text-center py-12">
                  <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-500">Nenhum cliente cadastrado</p>
                </td>
              </tr>
            ) : (
              filteredClientes.map((cliente) => (
                <tr key={cliente.id} className="table-row border-b border-white/5" data-testid={`cliente-row-${cliente.id}`}>
                  <td className="px-6 py-4 text-sm text-slate-300">{cliente.nome}</td>
                  <td className="px-6 py-4 text-sm text-slate-300">{cliente.telefone || '-'}</td>
                  <td className="px-6 py-4 text-sm text-slate-300">{cliente.email || '-'}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end space-x-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => gerarLinkMagico(cliente)}
                        className="text-emerald-400 hover:text-white hover:bg-white/5"
                        title="Gerar link de acesso ao portal"
                        data-testid={`link-cliente-${cliente.id}`}
                      >
                        <LinkIcon className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPerfilModal({ open: true, clienteId: cliente.id })}
                        className="text-electric-blue hover:text-white hover:bg-white/5"
                        title="Ver perfil do cliente"
                        data-testid={`perfil-cliente-${cliente.id}`}
                      >
                        <UserCircle className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(cliente.id)}
                        className="text-alert-red hover:text-white hover:bg-white/5"
                        data-testid={`delete-cliente-${cliente.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Dialog Link Mágico */}
      <Dialog open={linkDialog.open} onOpenChange={(o) => setLinkDialog({ ...linkDialog, open: o })}>
        <DialogContent className="bg-carbon border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Link de Acesso ao Portal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              Envie este link para <span className="text-white font-semibold">{linkDialog.cliente?.nome}</span>. Ele poderá acessar o portal sem precisar de senha. <span className="text-amber-400">Expira em 7 dias.</span>
            </p>
            <div className="bg-obsidian border border-white/10 rounded-md p-3 break-all text-xs text-emerald-300 font-mono" data-testid="link-magico-url">
              {linkDialog.url}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={copiarLink} className="bg-electric-blue hover:bg-blue-600 text-white" data-testid="copiar-link-btn">
                <Copy className="w-4 h-4 mr-2" /> Copiar
              </Button>
              <Button onClick={abrirWhatsApp} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="whatsapp-link-btn">
                <MessageCircle className="w-4 h-4 mr-2" /> Enviar WhatsApp
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Perfil do Cliente */}
      <ClientePerfilModal
        open={perfilModal.open}
        onOpenChange={(o) => setPerfilModal({ ...perfilModal, open: o })}
        clienteId={perfilModal.clienteId}
        onEditarCadastro={(cliente) => {
          setPerfilModal({ open: false, clienteId: null });
          handleEdit(cliente);
        }}
      />
    </div>
  );
}

export default Clientes;